import React, { useEffect, useState } from 'react';
import '../styles/AddLeave.css';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';

const WORKFORCE_QUERY = `
  query WorkforceForAssignLeave($page: Int, $limit: Int, $roles: [String!]) {
    getAllUser(page: $page, limit: $limit, roles: $roles) {
      data {
        id
        firstName
        lastName
        email
        employeeCode
        roles
      }
      totalPages
    }
  }
`;

const CREATE_LEAVE = `
  mutation CreateLeaveRequest($createLeaveRequestInput: CreateLeaveRequestInput!) {
    createLeaveRequest(createLeaveRequestInput: $createLeaveRequestInput) {
      id
      status
    }
  }
`;

const UPDATE_LEAVE_STATUS = `
  mutation UpdateLeaveRequestStatus($id: String!, $status: String!) {
    updateLeaveRequestStatus(id: $id, status: $status) {
      id
      status
    }
  }
`;

// Admins, managers, and cleaners (same groups as Work Force tabs)
const WORKFORCE_ROLES = [
  'ADMIN',
  'SUPER_ADMIN',
  'BRANCH_SUPER_ADMIN',
  'OPERATIONS_MANAGER',
  'SUPERVISOR',
  'FIELD_CLEANER',
];

function normalizeRole(role) {
  return String(role || '')
    .trim()
    .toUpperCase()
    .replace(/[/\s-]+/g, '_');
}

function hasAnyRole(roles = [], expected = []) {
  const normalized = expected.map(normalizeRole);
  return (roles || []).some((role) => normalized.includes(normalizeRole(role)));
}

function memberRoleLabel(roles = []) {
  if (hasAnyRole(roles, ['ADMIN', 'SUPER_ADMIN', 'BRANCH_SUPER_ADMIN'])) return 'Admin';
  if (hasAnyRole(roles, ['OPERATIONS_MANAGER', 'SUPERVISOR'])) return 'Manager';
  if (hasAnyRole(roles, ['FIELD_CLEANER'])) return 'Cleaner';
  return 'Staff';
}

async function fetchWorkforceMembers() {
  const byId = new Map();
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages) {
    const data = await graphqlRequest(WORKFORCE_QUERY, {
      page,
      limit: 100,
      roles: WORKFORCE_ROLES,
    });
    const result = data?.getAllUser;
    for (const user of result?.data || []) {
      byId.set(user.id, user);
    }
    totalPages = result?.totalPages ?? 1;
    page += 1;
  }

  return Array.from(byId.values()).sort((a, b) => {
    const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
    const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
    return nameA.localeCompare(nameB);
  });
}

function mapDurationType(type) {
  if (type === 'multiple') return 'Multiple Day';
  if (type === 'hour') return 'Hour';
  return 'Single Day';
}

export default function AssignLeave({ onClose, onLeaveAssigned }) {
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    employeeId: '',
    name: '',
    type: 'Casual Leave',
    durationType: 'single',
    date: '',
    startDate: '',
    endDate: '',
    hours: '',
    reason: '',
    status: 'Approved',
  });

  useEffect(() => {
    const loadEmployees = async () => {
      setLoadingEmployees(true);
      try {
        const rows = await fetchWorkforceMembers();
        setEmployees(rows);
        if (rows.length === 0) {
          toast.info('No team members found. Add staff in Work Force first.');
        }
      } catch (err) {
        toast.error(err?.message || 'Could not load team members.');
        setEmployees([]);
      } finally {
        setLoadingEmployees(false);
      }
    };
    void loadEmployees();
  }, []);

  const handleEmployeeChange = (employeeId) => {
    const selected = employees.find((e) => e.id === employeeId);
    setFormData((prev) => ({
      ...prev,
      employeeId,
      name: selected
        ? `${selected.firstName || ''} ${selected.lastName || ''}`.trim()
        : '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.employeeId) {
      toast.error('Please select a team member');
      return;
    }
    if (!formData.type) {
      toast.error('Please select a leave type');
      return;
    }
    if (!formData.reason?.trim()) {
      toast.error('Please enter a reason');
      return;
    }

    const duration = mapDurationType(formData.durationType);
    let startDate = '';

    if (formData.durationType === 'single') {
      if (!formData.date) {
        toast.error('Please select a date');
        return;
      }
      startDate = formData.date;
    } else if (formData.durationType === 'multiple') {
      if (!formData.startDate || !formData.endDate) {
        toast.error('Please select both start and end dates');
        return;
      }
      if (new Date(formData.startDate) > new Date(formData.endDate)) {
        toast.error('End date must be after start date');
        return;
      }
      startDate = formData.startDate;
    } else {
      if (!formData.date) {
        toast.error('Please select a date');
        return;
      }
      const hours = Number(formData.hours);
      if (!hours || hours < 1 || hours > 8) {
        toast.error('Please enter valid hours (1-8)');
        return;
      }
      startDate = formData.date;
    }

    const statusMap = {
      Approved: 'Approved',
      Pending: 'Pending',
      Declined: 'Rejected',
    };
    const targetStatus = statusMap[formData.status] || 'Approved';

    const input = {
      name: formData.name,
      employeeId: formData.employeeId,
      leaveType: formData.type,
      duration,
      startDate,
      reason: formData.reason.trim(),
    };

    if (duration === 'Multiple Day') {
      input.endDate = formData.endDate;
    }
    if (duration === 'Hour') {
      const hours = Number(formData.hours);
      input.hour = String(hours);
      input.startTime = '9:00 am';
      input.endTime = `${hours + 8}:00 pm`;
    }

    setSubmitting(true);
    try {
      const created = await graphqlRequest(CREATE_LEAVE, {
        createLeaveRequestInput: input,
      });

      const leaveId = created?.createLeaveRequest?.id;
      if (!leaveId) {
        throw new Error('Leave was not created.');
      }

      if (targetStatus !== 'Pending') {
        await graphqlRequest(UPDATE_LEAVE_STATUS, {
          id: leaveId,
          status: targetStatus,
        });
      }

      toast.success('Leave assigned successfully.');
      onLeaveAssigned?.();
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Could not assign leave.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="overlay-leave">
      <div className="leave-modal">
        <div className="modal-header">
          <h2>Assign Leave</h2>
          <span className="close-btn-leave" onClick={onClose}>
            ✖
          </span>
        </div>

        <form className="leave-form" onSubmit={handleSubmit}>
          <div className="form-group-leave">
            <label>Team Member</label>
            <select
              value={formData.employeeId}
              onChange={(e) => handleEmployeeChange(e.target.value)}
              required
              disabled={loadingEmployees || submitting || employees.length === 0}
            >
              <option value="">
                {loadingEmployees
                  ? 'Loading team members…'
                  : employees.length === 0
                    ? 'No team members available'
                    : 'Select team member'}
              </option>
              {employees.map((emp) => {
                const name = `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
                const role = memberRoleLabel(emp.roles);
                const code = emp.employeeCode ? ` (${emp.employeeCode})` : '';
                return (
                  <option key={emp.id} value={emp.id}>
                    {name}
                    {code}
                    {' — '}
                    {role}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="form-group-leave">
            <label>Leave Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              required
              disabled={submitting}
            >
              <option value="Casual Leave">Casual Leave</option>
              <option value="Sick Leave">Sick Leave</option>
              <option value="Annual Leave">Annual Leave</option>
              <option value="Maternity Leave">Maternity Leave</option>
              <option value="Paternity Leave">Paternity Leave</option>
              <option value="Personal Leave">Personal Leave</option>
            </select>
          </div>

          <div className="form-group-leave">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              required
              disabled={submitting}
            >
              <option value="Approved">Approved</option>
              <option value="Pending">Pending</option>
              <option value="Declined">Declined</option>
            </select>
          </div>

          <div className="form-group-leave">
            <label>Duration</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  name="duration"
                  value="single"
                  checked={formData.durationType === 'single'}
                  onChange={(e) => setFormData({ ...formData, durationType: e.target.value })}
                  disabled={submitting}
                />
                Single Day
              </label>
              <label>
                <input
                  type="radio"
                  name="duration"
                  value="multiple"
                  checked={formData.durationType === 'multiple'}
                  onChange={(e) => setFormData({ ...formData, durationType: e.target.value })}
                  disabled={submitting}
                />
                Multiple Day
              </label>
              <label>
                <input
                  type="radio"
                  name="duration"
                  value="hour"
                  checked={formData.durationType === 'hour'}
                  onChange={(e) => setFormData({ ...formData, durationType: e.target.value })}
                  disabled={submitting}
                />
                Hour
              </label>
            </div>

            {formData.durationType === 'single' ? (
              <div className="form-row-leave">
                <label className="label-leave">Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  disabled={submitting}
                />
              </div>
            ) : null}

            {formData.durationType === 'multiple' ? (
              <div>
                <div className="form-row-leave">
                  <label className="label-leave">Start Day</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="form-row-leave">
                  <label className="label-leave">End Day</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    min={formData.startDate}
                    required
                    disabled={submitting}
                  />
                </div>
              </div>
            ) : null}

            {formData.durationType === 'hour' ? (
              <div>
                <div className="form-row-leave">
                  <label className="label-leave">Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="form-row-leave">
                  <label className="label-leave">Hour</label>
                  <input
                    type="number"
                    min="1"
                    max="8"
                    placeholder="Max 8 hours"
                    value={formData.hours}
                    onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                    required
                    disabled={submitting}
                  />
                </div>
              </div>
            ) : null}
          </div>

          <div className="form-row-leave">
            <label className="label-leave">Reason</label>
            <textarea
              placeholder="Enter reason"
              className="textarea-leave"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              required
              disabled={submitting}
            />
          </div>

          <button
            type="submit"
            className="submit-btn"
            disabled={submitting || loadingEmployees || employees.length === 0}
          >
            {submitting ? 'Assigning…' : 'Assign Leave'}
          </button>
        </form>
      </div>
    </div>
  );
}
